import React, { useState, useEffect, useId } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { searchAddressByPostalCode, formatPostalCode } from '../../../utils/postalCode.js';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import {
  canManageClinic,
  HUB_CLINIC_EDIT_DENIED_MESSAGE
} from '../../../utils/hubPermissions.js';
import {
  getImagePreviewUrl,
  validateImageFile,
  uploadClinicLogo,
  deleteImage
} from '../../../utils/imageUpload.js';

/**
 * クリニック設定ページ
 */
const ClinicSettings = () => {
  const logoInputId = useId();
  const { clinic, user, onRefreshSession } = useOutletContext();
  const { showToast } = useToast();
  const canManage = canManageClinic(user);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // クリニック情報（基本情報 + ロゴURL）
  const [clinicData, setClinicData] = useState({
    name: clinic?.name || '',
    postalCode: clinic?.postalCode || '',
    address: clinic?.address || '',
    phone: clinic?.phone || '',
    email: clinic?.email || '',
    contactPerson: clinic?.contactPerson || '',
    logoUrl: clinic?.logoUrl || ''
  });

  // ロゴ: 新規選択ファイル・プレビュー・削除フラグ
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoRemove, setLogoRemove] = useState(false);

  useEffect(() => {
    setClinicData({
      name: clinic?.name || '',
      postalCode: clinic?.postalCode || '',
      address: clinic?.address || '',
      phone: clinic?.phone || '',
      email: clinic?.email || '',
      contactPerson: clinic?.contactPerson || '',
      logoUrl: clinic?.logoUrl || ''
    });
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemove(false);
  }, [clinic]);

  // 郵便番号の入力ハンドラー
  const handlePostalCodeChange = async (e) => {
    const formatted = formatPostalCode(e.target.value);
    const normalizedCode = formatted.replace(/[^\d]/g, '');
    
    setClinicData(prev => ({ ...prev, postalCode: formatted }));

    if (normalizedCode.length === 7) {
      setSearchingAddress(true);
      try {
        const result = await searchAddressByPostalCode(formatted);
        if (result.success) {
          setClinicData(prev => ({ ...prev, address: result.address }));
        }
      } catch (error) {
        console.error('住所検索エラー:', error);
      } finally {
        setSearchingAddress(false);
      }
    }
  };

  const handleLogoChange = async (e) => {
    if (!canManage) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error);
      showToast('clinic.logo.error', { message: validation.error });
      return;
    }
    setError('');
    try {
      const previewUrl = await getImagePreviewUrl(file);
      setLogoFile(file);
      setLogoPreview(previewUrl);
      setLogoRemove(false);
    } catch (err) {
      setError('画像の読み込みに失敗しました。');
      showToast('clinic.logo.error', { message: '画像の読み込みに失敗しました。' });
    }
  };

  const handleRemoveLogo = () => {
    if (!canManage) return;
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemove(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) {
      showToast('clinic.update.error', { message: HUB_CLINIC_EDIT_DENIED_MESSAGE });
      return;
    }
    setError('');
    setSuccess('');
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!canManage) return;
    setShowConfirmModal(false);
    setLoading(true);
    setError('');
    try {
      let logoUrlToSend = undefined;

      if (logoRemove) {
        if (clinic?.logoUrl) {
          try {
            await deleteImage(clinic.logoUrl);
          } catch (err) {
            console.warn('既存ロゴのStorage削除をスキップ:', err);
          }
        }
        logoUrlToSend = null;
      } else if (logoFile && clinic?.id) {
        const validation = validateImageFile(logoFile);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
        if (clinic?.logoUrl) {
          try {
            await deleteImage(clinic.logoUrl);
          } catch (err) {
            console.warn('既存ロゴのStorage削除をスキップ:', err);
          }
        }
        logoUrlToSend = await uploadClinicLogo(clinic.id, logoFile);
      }

      const body = {
        name: clinicData.name,
        postalCode: clinicData.postalCode,
        address: clinicData.address,
        phone: clinicData.phone,
        email: clinicData.email,
        contactPerson: clinicData.contactPerson
      };
      if (typeof logoUrlToSend !== 'undefined') {
        body.logoUrl = logoUrlToSend;
      }

      const response = await fetch(buildApiUrl('/api/clinic/update'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'クリニック情報の更新に失敗しました。');
      }

      setLogoFile(null);
      setLogoPreview(null);
      setLogoRemove(false);
      if (data.emailVerificationRequired) {
        setClinicData((prev) => ({ ...prev, email: clinic?.email || prev.email }));
        setSuccess(data.message || '確認メールを送信しました。新しいメールアドレスの確認後に反映されます。');
        showToast('clinic.update.success');
      } else {
        setSuccess('クリニック情報を更新しました。');
        showToast('clinic.update.success');
      }
      setShowSuccessModal(true);
      if (typeof onRefreshSession === 'function') {
        onRefreshSession();
      }
    } catch (err) {
      setError(err.message);
      showToast('clinic.update.error', { message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) return;
    setShowConfirmModal(false);
    setShowSuccessModal(false);
  }, [canManage]);

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">クリニック設定</h1>
      </div>

      <div className="hub-main-content">

        {!canManage && (
          <div style={{
            background: 'var(--hub-warning-light)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--hub-warning)' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ color: 'var(--hub-text-primary)' }}>
              {HUB_CLINIC_EDIT_DENIED_MESSAGE}
            </span>
          </div>
        )}

        {clinic?.pendingEmail && (
          <div style={{
            background: 'var(--hub-info-light, #eff6ff)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
          }}>
            <p style={{ margin: '0 0 8px', color: 'var(--hub-text-primary)', fontWeight: 600 }}>
              メールアドレス変更の確認待ち
            </p>
            <p style={{ margin: '0 0 12px', color: 'var(--hub-text-secondary)', fontSize: '0.9375rem' }}>
              新しいアドレス（{clinic.pendingEmail}）宛に確認メールを送信済みです。確認が完了するまで連絡先は変更前のままです。
            </p>
            {canManage && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <Link
                  to="/verify-email/pending"
                  state={{
                    email: clinic.pendingEmail,
                    clinicName: clinic.name || clinicData.name || '',
                    clinicId: clinic.id || user?.clinicId || '',
                    userId: user?.userId || '',
                    purpose: 'email-change',
                  }}
                  className="hub-btn hub-btn-primary"
                >
                  確認メールを再送する
                </Link>
                <button
                  type="button"
                  className="hub-btn hub-btn-secondary"
                  disabled={loading}
                  onClick={async () => {
                    setLoading(true);
                    setError('');
                    try {
                      const response = await fetch(buildApiUrl('/api/clinic/cancel-pending-email'), {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({}),
                      });
                      const data = await response.json().catch(() => ({}));
                      if (!response.ok) {
                        throw new Error(data.message || '変更申請の取消に失敗しました。');
                      }
                      setSuccess('メール変更申請を取り消しました。');
                      if (typeof onRefreshSession === 'function') onRefreshSession();
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  変更申請を取り消す
                </button>
              </div>
            )}
          </div>
        )}

        {/* クリニックコード・ID */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0" style={{ marginBottom: 'var(--hub-space-xl)' }}>
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-lg)'
          }}>
            クリニック識別情報
          </h2>

          {clinic?.code && (
            <div style={{ marginBottom: 'var(--hub-space-md)' }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                クリニックコード
              </div>
              <div style={{
                background: 'var(--hub-bg-tertiary)',
                padding: 'var(--hub-space-md)',
                borderRadius: 'var(--hub-radius-md)',
                fontFamily: 'var(--hub-font-mono)',
                fontSize: '0.9375rem',
              }}>
                {clinic.code}
              </div>
            </div>
          )}
          
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
              クリニックID
            </div>
            <div style={{
              background: 'var(--hub-bg-tertiary)',
              padding: 'var(--hub-space-md)',
              borderRadius: 'var(--hub-radius-md)',
              fontFamily: 'var(--hub-font-mono)',
              fontSize: '0.9375rem',
              wordBreak: 'break-all'
            }}>
              {clinic?.id || user?.clinicId || '不明'}
            </div>
          </div>
        </div>

        {/* クリニック情報 */}
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-2" data-panel-index="1">
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.125rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-xl)'
          }}>
            基本情報
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="hub-form-group">
              <label className="hub-form-label">クリニック名</label>
              <input
                type="text"
                className="hub-form-input"
                value={clinicData.name}
                onChange={(e) => setClinicData({ ...clinicData, name: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">
                郵便番号
                {searchingAddress && (
                  <span style={{ marginLeft: '8px', color: 'var(--hub-accent-primary)', fontSize: '0.875rem' }}>
                    （住所を検索中...）
                  </span>
                )}
              </label>
              <input
                type="text"
                className="hub-form-input"
                value={clinicData.postalCode}
                onChange={handlePostalCodeChange}
                maxLength="8"
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">住所</label>
              <input
                type="text"
                className="hub-form-input"
                value={clinicData.address}
                onChange={(e) => setClinicData({ ...clinicData, address: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">電話番号</label>
              <input
                type="tel"
                className="hub-form-input"
                value={clinicData.phone}
                onChange={(e) => setClinicData({ ...clinicData, phone: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">メールアドレス</label>
              <input
                type="email"
                className="hub-form-input"
                value={clinicData.email}
                onChange={(e) => setClinicData({ ...clinicData, email: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">担当者名</label>
              <input
                type="text"
                className="hub-form-input"
                value={clinicData.contactPerson}
                onChange={(e) => setClinicData({ ...clinicData, contactPerson: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label">クリニックロゴ（任意）</label>
              <p className="hub-form-hint" style={{ marginBottom: 'var(--hub-space-md)' }}>
                JPEG, PNG, GIF, WebP（5MB以下）
              </p>

              <div className="hub-logo-upload">
                {logoRemove && canManage ? (
                  <div className="hub-logo-upload-notice">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span className="hub-logo-upload-notice-text">
                      保存するとロゴが削除されます。取り消す場合は「削除をやめる」を押してください。
                    </span>
                    <button
                      type="button"
                      className="hub-btn hub-btn-secondary"
                      onClick={() => { setLogoRemove(false); setError(''); }}
                    >
                      削除をやめる
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="hub-logo-upload-preview">
                      {(clinicData.logoUrl || logoPreview) ? (
                        <img
                          src={logoPreview || clinicData.logoUrl}
                          alt="ロゴプレビュー"
                          className="hub-logo-upload-image"
                        />
                      ) : (
                        <div className="hub-logo-upload-placeholder">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                          <span>ロゴ未登録</span>
                        </div>
                      )}
                    </div>

                    <div className="hub-logo-upload-body">
                      <div className="hub-logo-upload-status">
                        <span className="hub-logo-upload-status-label">
                          {(clinicData.logoUrl || logoPreview) ? '登録済みのロゴ' : 'ロゴ画像'}
                        </span>
                        <span className="hub-logo-upload-status-hint">
                          {(clinicData.logoUrl || logoPreview)
                            ? (logoFile
                              ? `新しいファイル: ${logoFile.name}`
                              : 'サイドバーなどに表示されます。変更する場合は別の画像を選択してください。')
                            : 'クリニックのロゴ画像をアップロードできます。'}
                        </span>
                      </div>

                      <div className="hub-logo-upload-actions">
                        {canManage ? (
                          <>
                            <div className="hub-file-input-wrap">
                              <input
                                id={logoInputId}
                                type="file"
                                className="hub-file-input"
                                accept="image/*"
                                onChange={handleLogoChange}
                              />
                              <label htmlFor={logoInputId} className="hub-btn hub-btn-secondary hub-file-input-label">
                                {(clinicData.logoUrl || logoPreview) ? '画像を変更' : 'ファイルを選択'}
                              </label>
                            </div>
                            {(clinicData.logoUrl || logoPreview) && (
                              <button
                                type="button"
                                className="hub-btn hub-btn-danger-outline"
                                onClick={handleRemoveLogo}
                              >
                                ロゴを削除
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="hub-btn hub-btn-secondary"
                              disabled
                              title={HUB_CLINIC_EDIT_DENIED_MESSAGE}
                            >
                              {(clinicData.logoUrl || logoPreview) ? '画像を変更' : 'ファイルを選択'}
                            </button>
                            {(clinicData.logoUrl || logoPreview) && (
                              <button
                                type="button"
                                className="hub-btn hub-btn-danger-outline"
                                disabled
                                title={HUB_CLINIC_EDIT_DENIED_MESSAGE}
                              >
                                ロゴを削除
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--hub-space-md)',
              marginTop: 'var(--hub-space-sm)'
            }}>
              <button
                type="submit"
                className="hub-btn hub-btn-primary"
                disabled={!canManage || loading}
                title={!canManage ? HUB_CLINIC_EDIT_DENIED_MESSAGE : undefined}
                style={{ flexShrink: 0 }}
              >
                {loading ? <LoadingSpinner size="small" /> : '保存'}
              </button>

              {canManage && error && (
                  <div style={{
                    background: 'var(--hub-danger-light)',
                    color: 'var(--hub-danger)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: 'var(--hub-radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hub-space-sm)',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    lineHeight: 1.5
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="9"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    {error}
                  </div>
                )}

              {canManage && success && (
                  <div style={{
                    background: 'var(--hub-success-light)',
                    color: 'var(--hub-success)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: 'var(--hub-radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hub-space-sm)',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    lineHeight: 1.5
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {success}
                  </div>
              )}
            </div>
          </form>
        </div>

        {showConfirmModal && canManage && (
          <div
            className="hub-modal-overlay"
            onClick={() => setShowConfirmModal(false)}
          >
            <div
              className="hub-card"
              style={{
                width: '100%',
                maxWidth: '480px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.8) 100%)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{
                fontFamily: 'var(--hub-font-display)',
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: 'var(--hub-space-md)'
              }}>
                変更内容を保存しますか？
              </h2>
              <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-lg)' }}>
                クリニック情報が上書きされます。よろしいですか？
              </p>
              <div style={{ display: 'flex', gap: 'var(--hub-space-md)', justifyContent: 'flex-end' }}>
                <button
                  className="hub-btn hub-btn-ghost"
                  onClick={() => setShowConfirmModal(false)}
                >
                  キャンセル
                </button>
                <button
                  className="hub-btn hub-btn-primary"
                  onClick={handleConfirmSave}
                >
                  保存する
                </button>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && canManage && (
          <div
            className="hub-modal-overlay"
            onClick={() => setShowSuccessModal(false)}
          >
            <div
              className="hub-card"
              style={{
                width: '100%',
                maxWidth: '480px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.8) 100%)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{
                fontFamily: 'var(--hub-font-display)',
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: 'var(--hub-space-md)'
              }}>
                更新完了
              </h2>
              <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-lg)' }}>
                クリニック情報を更新しました。
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="hub-btn hub-btn-primary"
                  onClick={() => setShowSuccessModal(false)}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClinicSettings;

